import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { paymentMethods, IStripeSource } from 'stripe';
import { Entry } from 'contentful';
import { environment } from '../../../environments/environment';
import { FunctionsService } from '../../services/functions/functions.service';
import { ProfileService } from '../../services/profile/profile.service';
import { LocalizationService } from '../../services/localization/localization.service';
import { ScriptService } from '../../services/script/script.service';

@Component({
  selector: 'app-default-payment-method',
  templateUrl: './default-payment-method.component.html',
  styleUrls: ['./default-payment-method.component.scss']
})
export class DefaultPaymentMethodComponent implements OnInit {
  public defaultPaymentMethod$: Observable<
    paymentMethods.IPaymentMethod | IStripeSource
  >;
  public cardError: string | undefined;
  public updatePaymentMethodError: { message?: string } | undefined;
  public updatePaymentMethodProcessing = false;
  public cardComplete = false;
  public editPaymentMethod = false;

  @Input() public content: Entry<any> | undefined;
  @ViewChild('cardElement', { static: true }) cardElement:
    | ElementRef
    | undefined;

  constructor(
    private fnService: FunctionsService,
    profileService: ProfileService,
    private localizationService: LocalizationService,
    private scriptService: ScriptService
  ) {
    this.defaultPaymentMethod$ = profileService.defaultPaymentMethod();
  }

  private stripe: any;
  private card: any;

  ngOnInit() {
    if (!this.cardElement) {
      throw new Error('#cardElement not found');
    }

    const stripeLocale =
      this.localizationService.locale.value === 'en-US'
        ? 'en'
        : this.localizationService.locale.value;

    this.scriptService
      .load('stripe')
      .then(() => {
        this.stripe = Stripe(environment.stripe.public_api_key, {
          locale: stripeLocale
        });

        this.card = this.stripe.elements().create('card');

        this.card.mount(this.cardElement.nativeElement);
        this.card.on('change', event => {
          this.updatePaymentMethodError = undefined;
          this.cardComplete = event.complete;
          this.cardError = event.error ? event.error.message : undefined;
        });
      })
      .catch(error => console.error(error));
  }

  public async updatePaymentMethod() {
    this.updatePaymentMethodProcessing = true;
    this.updatePaymentMethodError = undefined;
    try {
      const paymentMethod = await this.createPaymentMethod(this.card);
      await this.registerNewPaymentMethod(paymentMethod.id);
      this.updatePaymentMethodError = undefined;
      this.editPaymentMethod = false;
      this.card.clear();
    } catch (err) {
      this.updatePaymentMethodError = err;
    }
    this.updatePaymentMethodProcessing = false;
  }

  private async createPaymentMethod(card: stripe.elements.Element) {
    const resp = await this.stripe.createPaymentMethod('card', card);
    if (resp.error || !resp.paymentMethod) {
      console.error('Stripe.js createPaymentMethod error', resp.error);
      throw resp.error;
    }
    return resp.paymentMethod;
  }

  private async registerNewPaymentMethod(pmId: string) {
    try {
      const setupIntentClientSecret = await this.fnService.createSetupIntent();
      const { error } = await this.stripe.confirmCardSetup(setupIntentClientSecret, { payment_method: pmId })
      if (error) {
        throw error
      } else {
        await this.fnService.setDefaultPaymentMethod(pmId);
      }
    } catch (err) {
      if (err.message === 'StripeCardError') {
        console.error('Backend updatePaymentMethod error', err.details);
        throw err.details;
      }
      throw err;
    }
  }
}
